import {
  Button,
  Loading,
  ModalContent,
  ModalLoading,
  Modal,
} from '@clearcover/atlas';
import {
  AddPaymentMethodPayloadType,
  AllowedPaymentMethod,
  GetAllowedPaymentMethodsResponseType,
  GetPaymentMethodConfigResponseType,
  GetPaymentsSummaryByTermNumberResponse,
} from '@clearcover/core-app-types';
import { PolicyContext } from 'Policies/utils';
import { FormModalFooter, useToast } from 'Shared/components';
import { RadioButtonGroup } from 'Shared/components/FormInput/Variants';
import { FormModalHeader } from 'Shared/components/FormModalHeader';
import { useProcessOneScripts } from 'Shared/hooks';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { LazytApiExecuteFuncType } from 'Shared/services/Api/makeHooks';
import { AxiosPromise } from 'axios';
import classNames from 'classnames';
import { Formik } from 'formik';
import React, { FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { AdyenAddPaymentForm } from './AdyenAddPaymentForm';
import { IconsList } from './IconsList';
import styles from './styles.module.css';

type AddPaymentMethodFormPropsType = {
  isProcessOne: boolean;
  hasAdyenPaymentMethod: boolean;
  invoiceId?: string;
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
  close: () => void;
};

export type SubmitNewPaymentMethodType = (
  values: AddPaymentMethodPayloadType['paymentMethod']
) => Promise<void>;

export type AddPaymentMethodFormikType = {
  type: string;
  adyen: {
    submit: () => void;
    isValid: boolean;
  };
};

export const addPaymentMethodInitialValues: AddPaymentMethodFormikType = {
  type: 'ENCRYPTED_CREDIT_CARD',
  adyen: { submit: () => {}, isValid: false },
};

export const AddPaymentMethodForm: FC<AddPaymentMethodFormPropsType> = ({
  close,
  isProcessOne,
  hasAdyenPaymentMethod,
  invoiceId,
  fetchBillingData,
}) => {
  const [showDefaultPaymentModal, setShowDefaultPaymentModal] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState<
    AddPaymentMethodPayloadType['paymentMethod'] | null
  >(null);
  const [pendingPromiseResolvers, setPendingPromiseResolvers] = useState<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);
  const { toast } = useToast();
  const {
    person,
    details: { configDiscoveryParams },
  } = useContext(PolicyContext);
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const [{ data, loading }] = useApi<GetPaymentMethodConfigResponseType>(
    `/payments/config?configDiscoveryParams=${configDiscoveryParams}`
  );
  const [, addPaymentMethod] = useLazyApi<GetPaymentMethodConfigResponseType>({
    url: `/policies/${policyId}/addPaymentMethod`,
    method: 'POST',
  });
  const [
    { data: allowedPaymentMethods, loading: loadingAllowedMethods },
  ] = useNewApiV2<GetAllowedPaymentMethodsResponseType>({
    url: `/payments/allowedPaymentMethods?policyId=${policyId}&configDiscoveryParams=${configDiscoveryParams}`,
  });

  const PAYMENT_METHOD_FORM_SCHEMA = Yup.object({
    type: Yup.string().required('Required'),
  });

  const executePaymentMethodSubmission = (
    values: AddPaymentMethodPayloadType['paymentMethod']
  ) => {
    return addPaymentMethod({
      data: {
        paymentMethod: values,
        // If we're switching from ProcessOne to Adyen, we need to pass the invoiceId.  This will
        // make the new payment method become the default.
        invoiceId: isProcessOne ? invoiceId : undefined,
      },
    }).then(() => {
      close();
      fetchBillingData();
      toast({
        message: 'Payment method has been successfully added',
        type: 'success',
        withSecondaryNav: false,
      });
    });
  };

  const submitForm: SubmitNewPaymentMethodType = values => {
    // Check if user has no Adyen payment methods and show modal
    if (!hasAdyenPaymentMethod && data?.adyenForNewPaymentMethods) {
      return new Promise((resolve, reject) => {
        setPendingPaymentData(values);
        setPendingPromiseResolvers({ resolve, reject });
        setShowDefaultPaymentModal(true);
      });
    }

    return executePaymentMethodSubmission(values);
  };

  const handleModalContinue = () => {
    if (pendingPaymentData && pendingPromiseResolvers) {
      executePaymentMethodSubmission(pendingPaymentData)
        .then(() => {
          // Resolve the promise to let AdyenAddPaymentForm know we're done
          pendingPromiseResolvers.resolve();
        })
        .catch(err => {
          toast({
            message: err?.metadata?.responseBody?.message || err.message,
            type: 'error',
            withSecondaryNav: false,
          });
          // Reject the promise to let AdyenAddPaymentForm handle the error
          pendingPromiseResolvers.reject(err);
        });
    }
    setShowDefaultPaymentModal(false);
    setPendingPaymentData(null);
    setPendingPromiseResolvers(null);
  };

  const handleModalCancel = () => {
    // Reject the promise to let AdyenAddPaymentForm know the operation was cancelled
    if (pendingPromiseResolvers) {
      pendingPromiseResolvers.reject(new Error('User cancelled the operation'));
    }
    setShowDefaultPaymentModal(false);
    setPendingPaymentData(null);
    setPendingPromiseResolvers(null);
  };

  let paymentMethodOptions = loadingAllowedMethods
    ? {}
    : allowedPaymentMethods.reduce((acc: {}, method: AllowedPaymentMethod) => {
        return {
          ...acc,
          [method.type]: method.label,
        };
      }, {});

  return (
    <Formik
      initialValues={addPaymentMethodInitialValues}
      onSubmit={(values, { setSubmitting }) => {
        values.adyen.submit();
        !values.adyen.isValid && setSubmitting(false);
      }}
      validationSchema={PAYMENT_METHOD_FORM_SCHEMA}
    >
      {({ values, setFieldValue }) => (
        <>
          <FormModalHeader>Add new payment method</FormModalHeader>
          <ModalContent>
            <div className={styles.contentContainer}>
              {loading || loadingAllowedMethods ? (
                <ModalLoading />
              ) : (
                <div className={styles.content}>
                  <RadioButtonGroup
                    className={styles.switch}
                    name="payment-options"
                    sizeVariant="extraLarge"
                    value={values.type}
                    onChange={value => {
                      setFieldValue('type', value);
                    }}
                    label=""
                    other={{
                      ENCRYPTED_CREDIT_CARD: (
                        <IconsList iconType="creditCard" />
                      ),
                      ENCRYPTED_ACH: <IconsList iconType="bank" />,
                    }}
                    options={{ ...paymentMethodOptions }}
                    disabled={values.adyen === undefined}
                  />
                  {isProcessOne && !data.adyenForNewPaymentMethods ? (
                    <div className={classNames('fs-exclude', styles.form)}>
                      <ProcessOneSavePaymentMethod
                        addPaymentMethod={addPaymentMethod}
                        policyId={policyId}
                        close={close}
                        paymentCategory={
                          values.type === 'ENCRYPTED_CREDIT_CARD'
                            ? 'CreditCard'
                            : 'ECheck'
                        }
                      />
                    </div>
                  ) : (
                    <div
                      className={classNames(
                        'fs-exclude',
                        styles.form,
                        styles.adyenForm,
                        values.type === 'ENCRYPTED_CREDIT_CARD'
                          ? styles.adyenCreditCard
                          : styles.adyenAch
                      )}
                      data-testid="adyen-payment-form"
                    >
                      <AdyenAddPaymentForm
                        paymentType={values.type}
                        name={person.name}
                        config={data.config}
                        submitForm={submitForm}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </ModalContent>
          <FormModalFooter identifier="add-payment" close={close} />
          {/* Default Payment Method Modal */}
          {showDefaultPaymentModal && (
            <Modal
              variant="normal"
              controlled={true}
              open={showDefaultPaymentModal}
              handleClose={handleModalCancel}
            >
              {() => (
                <>
                  <ModalContent className={styles.defaultPaymentModalContainer}>
                    <div className={styles.defaultPaymentModalContent}>
                      <h2 className={styles.defaultPaymentModalTitle}>
                        This will become the default payment method.
                      </h2>
                      <p className={styles.defaultPaymentModalBody}>
                        Due to a system update, the payment method you add now
                        will become your default. This is a one-time change. To
                        set a different default, add a new payment method, and
                        then visit Payments & Billing to select it.
                      </p>
                      <div className={styles.defaultPaymentModalButtons}>
                        <Button
                          variant="secondary"
                          onClick={handleModalCancel}
                          data-testid="default-payment-modal-cancel"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleModalContinue}
                          data-testid="default-payment-modal-continue"
                        >
                          Continue
                        </Button>
                      </div>
                    </div>
                  </ModalContent>
                </>
              )}
            </Modal>
          )}
        </>
      )}
    </Formik>
  );
};

export const ProcessOneSavePaymentMethod: FC<{
  paymentCategory: 'ECheck' | 'CreditCard';
  policyId: string;
  close: () => void;
  addPaymentMethod: LazytApiExecuteFuncType<GetPaymentMethodConfigResponseType>;
}> = ({ paymentCategory, addPaymentMethod, policyId, close }) => {
  const { toast } = useToast();
  const { scriptLoaded } = useProcessOneScripts();
  const processOneContainerSelector = '#portalOneContainer';
  const [, requestSessionId] = useLazyApi<{ sessionId: string }>({
    url: `/payments/createProcessOneSessionId`,
    method: 'POST',
  });

  const openProcessOne = async () => {
    const sessionRes = await requestSessionId();

    window
      .$(processOneContainerSelector)
      .portalOne()
      .data('portalOne')
      .savePaymentMethod({
        paymentCategory,
        clientReferenceData1: policyId,
        confirmationDisplay: 'false',
        sessionId: sessionRes.data.sessionId,
      });
    window
      .$(processOneContainerSelector)
      .on('portalOne.saveComplete', function(
        _: any,
        data: { tokenId: string }
      ) {
        addPaymentMethod({
          data: {
            paymentMethod: {
              type: 'TOKENIZED',
              token: data.tokenId,
              paymentProcessor: 'PROCESS_ONE',
            },
          },
        })
          .then(() => {
            toast({
              message: 'Payment method has been successfully added',
              type: 'success',
              withSecondaryNav: false,
            });
            close();
          })
          .catch((err: any) => {
            toast({
              message: err?.metadata?.responseBody?.message || err.message,
              type: 'error',
              withSecondaryNav: false,
            });
          });
      });
  };
  if (!scriptLoaded) {
    return <Loading />;
  }
  return (
    <>
      <Button
        data-testid={'add-process-one-payment'}
        block
        variant="secondary"
        onClick={openProcessOne}
      >
        Add {paymentCategory === 'CreditCard' ? 'Credit Card' : 'Bank Account'}
      </Button>
      <div id="portalOneContainer"></div>
    </>
  );
};
